import { getBarcodeDataURL, type BarcodeType } from './barcodeFormats';
import type { ProductBinLocation } from '../types/warehouse';
import { formatBinLocationCompact } from './binLocationFormat';

export type LocationPosition = 'above' | 'below';
export type TextStyle = 'normal' | 'bold' | 'italic';

export interface LabelTemplate {
  id: string;
  name: string;
  width: number;
  height: number;
  unit: 'in' | 'mm';
  showName: boolean;
  showPrice: boolean;
  showSku: boolean;
  showDate: boolean;
  showLocation: boolean;
  showLogo: boolean;
  fontSize: 'small' | 'medium' | 'large';
  orientation: 'portrait' | 'landscape';
  /** When showLocation is true: 'above' = before barcode, 'below' = after barcode. Default 'below'. */
  locationPosition?: LocationPosition;
  /** Text style for label text. Default 'normal'. */
  textStyle?: TextStyle;
  /** Hex color for main label text (e.g. #111111). Default '#111111'. */
  textColor?: string;
}

export interface LabelData {
  barcode: string;
  codeType: BarcodeType;
  name?: string;
  price?: number;
  sku?: string;
  location?: string;
  binLocation?: ProductBinLocation | null;
  category?: string;
  date?: Date;
}

export interface LabelRenderOptions {
  barWidth?: number;
  barHeight?: number;
  margin?: number;
}

// Bin-location formatting now lives in a single source (`./binLocationFormat`),
// so warehouse labels render bins identically to pick lists and reports (they
// previously diverged: labels omitted the '-' separators and zero-padding).
export function formatLocationForLabel(
  location?: string,
  binLocation?: ProductBinLocation | null
): string {
  if (binLocation) {
    return formatBinLocationCompact(binLocation);
  }
  return location || '';
}

function getFontSize(size: 'small' | 'medium' | 'large'): string {
  switch (size) {
    case 'small': return '10px';
    case 'medium': return '12px';
    case 'large': return '14px';
    default: return '12px';
  }
}

function getNameFontSize(size: 'small' | 'medium' | 'large'): string {
  switch (size) {
    case 'small': return '11px';
    case 'medium': return '14px';
    case 'large': return '16px';
    default: return '14px';
  }
}

function getPriceFontSize(size: 'small' | 'medium' | 'large'): string {
  switch (size) {
    case 'small': return '12px';
    case 'medium': return '14px';
    case 'large': return '18px';
    default: return '14px';
  }
}

function getTextStyleCss(style: TextStyle = 'normal'): string {
  switch (style) {
    case 'bold': return 'font-weight: 700; font-style: normal;';
    case 'italic': return 'font-weight: normal; font-style: italic;';
    default: return 'font-weight: normal; font-style: normal;';
  }
}

const DEFAULT_TEXT_COLOR = '#111111';

export function generateLabelHTML(
  data: LabelData,
  template: LabelTemplate,
  options: LabelRenderOptions = {}
): string {
  const { barWidth = 3, barHeight = 100, margin = 20 } = options;
  const fontSize = getFontSize(template.fontSize);
  const nameFontSize = getNameFontSize(template.fontSize);
  const priceFontSize = getPriceFontSize(template.fontSize);
  const locPos = template.locationPosition ?? 'below';
  const textStyle = template.textStyle ?? 'normal';
  const textColor = template.textColor ?? DEFAULT_TEXT_COLOR;
  const styleCss = getTextStyleCss(textStyle);
  const colorCss = `color: ${textColor};`;

  const dataUrl = getBarcodeDataURL(data.barcode, data.codeType, {
    barWidth,
    barHeight,
    margin
  });

  if (!dataUrl) return '';

  const locationDisplay = formatLocationForLabel(data.location, data.binLocation);
  const dateDisplay = data.date ? data.date.toLocaleDateString() : new Date().toLocaleDateString();

  const locationColor = '#0d9488'; /* teal accent; keep fixed per plan */
  const locationBlock =
    template.showLocation && locationDisplay
      ? `<div class="location-info" style="font-size: ${fontSize}; ${styleCss} color: ${locationColor};">${escapeHtml(locationDisplay)}</div>`
      : '';

  const labelParts: string[] = [];

  if (template.showName && data.name) {
    labelParts.push(`<div class="item-name" style="font-size: ${nameFontSize}; ${styleCss} ${colorCss}">${escapeHtml(data.name)}</div>`);
  }

  if (locPos === 'above' && locationBlock) {
    labelParts.push(locationBlock);
  }

  labelParts.push(`<img src="${dataUrl}" alt="Barcode" class="barcode-image" />`);
  labelParts.push(`<div class="barcode-value" style="${colorCss}">${escapeHtml(data.barcode)}</div>`);

  if (template.showPrice && data.price !== undefined && data.price > 0) {
    labelParts.push(`<div class="price-info" style="font-size: ${priceFontSize}; ${styleCss} ${colorCss}">$${data.price.toFixed(2)}</div>`);
  }

  if (template.showSku && data.sku) {
    labelParts.push(`<div class="sku-info" style="font-size: ${fontSize}; ${styleCss} ${colorCss}">SKU: ${escapeHtml(data.sku)}</div>`);
  }

  if (locPos === 'below' && locationBlock) {
    labelParts.push(locationBlock);
  }

  if (template.showDate) {
    labelParts.push(`<div class="date-info" style="font-size: ${fontSize}; ${styleCss} ${colorCss}">${dateDisplay}</div>`);
  }

  return labelParts.join('\n');
}

export function generateSingleLabelPrintHTML(
  data: LabelData,
  template: LabelTemplate,
  options: LabelRenderOptions = {}
): string {
  const labelContent = generateLabelHTML(data, template, options);
  if (!labelContent) return '';

  const fontSize = getFontSize(template.fontSize);

  return `
    <!DOCTYPE html>
    <html lang="en">
      <head>
        <meta charset="UTF-8">
        <title>Print Label - ${escapeHtml(data.name || data.barcode)}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body {
            font-family: system-ui, -apple-system, sans-serif;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            min-height: 100vh;
            padding: 40px;
            background: white;
            font-size: ${fontSize};
          }
          .label-wrapper {
            text-align: center;
            background: white;
            padding: 24px;
            border-radius: 8px;
          }
          .item-name {
            font-weight: 600;
            color: #111;
            margin-bottom: 12px;
          }
          .barcode-image {
            display: block;
            margin: 0 auto;
            max-width: 100%;
            height: auto;
            image-rendering: crisp-edges;
          }
          .barcode-value {
            font-family: 'SF Mono', 'Monaco', 'Consolas', monospace;
            font-size: 11px;
            color: #666;
            margin-top: 6px;
          }
          .price-info {
            font-weight: 700;
            color: #111;
            margin-top: 8px;
          }
          .sku-info {
            color: #888;
            margin-top: 4px;
          }
          .location-info {
            color: #0d9488;
            font-weight: 600;
            margin-top: 6px;
          }
          .date-info {
            color: #999;
            margin-top: 4px;
          }
          .no-print {
            margin-top: 30px;
          }
          .print-button {
            background: #0d9488;
            color: white;
            border: none;
            padding: 12px 24px;
            font-size: 16px;
            border-radius: 6px;
            cursor: pointer;
            font-weight: 500;
          }
          .print-button:hover {
            background: #0f766e;
          }
          @media print {
            .no-print { display: none !important; }
            @page { margin: 15mm; }
          }
          @media screen {
            body { background: #f3f4f6; }
            .label-wrapper { box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1); }
          }
        </style>
      </head>
      <body>
        <div class="label-wrapper">
          ${labelContent}
          <div class="no-print">
            <button class="print-button" onclick="window.print()">Print Label</button>
          </div>
        </div>
      </body>
    </html>
  `;
}

export function generateBulkLabelsPrintHTML(
  labels: LabelData[],
  template: LabelTemplate,
  labelsPerPage: number = 4,
  options: LabelRenderOptions = {}
): string {
  if (labels.length === 0) return '';

  const gridCols = labelsPerPage <= 2 ? 1 : labelsPerPage <= 4 ? 2 : 3;
  const fontSize = getFontSize(template.fontSize);

  const labelsHtml = labels.map(data => {
    const labelContent = generateLabelHTML(data, template, options);
    return labelContent ? `<div class="barcode-card">${labelContent}</div>` : '';
  }).filter(Boolean).join('\n');

  return `
    <!DOCTYPE html>
    <html lang="en">
      <head>
        <meta charset="UTF-8">
        <title>Print Labels (${labels.length})</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body {
            font-family: system-ui, -apple-system, sans-serif;
            background: white;
            padding: 20px;
            font-size: ${fontSize};
          }
          .header {
            text-align: center;
            margin-bottom: 24px;
            padding-bottom: 16px;
            border-bottom: 2px solid #e5e7eb;
          }
          .header h1 {
            font-size: 20px;
            color: #111;
          }
          .barcode-grid {
            display: grid;
            grid-template-columns: repeat(${gridCols}, 1fr);
            gap: 16px;
          }
          .barcode-card {
            border: 1px solid #e5e7eb;
            border-radius: 8px;
            padding: 16px;
            text-align: center;
            page-break-inside: avoid;
          }
          .item-name {
            font-weight: 600;
            color: #111;
            margin-bottom: 8px;
          }
          .barcode-image {
            display: block;
            margin: 0 auto;
            max-width: 100%;
            height: auto;
            image-rendering: crisp-edges;
          }
          .barcode-value {
            font-family: 'SF Mono', 'Monaco', 'Consolas', monospace;
            font-size: 11px;
            color: #666;
            margin-top: 4px;
          }
          .price-info {
            font-weight: 700;
            color: #111;
            margin-top: 4px;
          }
          .sku-info {
            font-size: 10px;
            color: #888;
            margin-top: 2px;
          }
          .location-info {
            font-size: 11px;
            color: #0d9488;
            font-weight: 600;
            margin-top: 4px;
          }
          .date-info {
            font-size: 10px;
            color: #999;
            margin-top: 2px;
          }
          .no-print {
            text-align: center;
            margin-top: 24px;
            padding-top: 16px;
            border-top: 2px solid #e5e7eb;
          }
          .print-button {
            background: #0d9488;
            color: white;
            border: none;
            padding: 12px 32px;
            font-size: 16px;
            border-radius: 6px;
            cursor: pointer;
            margin-right: 12px;
          }
          .print-button:hover {
            background: #0f766e;
          }
          .close-button {
            background: #6b7280;
            color: white;
            border: none;
            padding: 12px 32px;
            font-size: 16px;
            border-radius: 6px;
            cursor: pointer;
          }
          @media print {
            .no-print { display: none !important; }
            @page { margin: 15mm; }
          }
          @media screen {
            body { background: #f3f4f6; max-width: 900px; margin: 0 auto; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>Generated Labels (${labels.length})</h1>
        </div>
        <div class="barcode-grid">${labelsHtml}</div>
        <div class="no-print">
          <button class="print-button" onclick="window.print()">Print</button>
          <button class="close-button" onclick="window.close()">Close</button>
        </div>
      </body>
    </html>
  `;
}

function escapeHtml(text: string): string {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

export function openPrintWindow(html: string): boolean {
  const printWindow = window.open('', '_blank', 'width=800,height=600');
  if (!printWindow) {
    return false;
  }
  printWindow.document.write(html);
  printWindow.document.close();
  return true;
}

/**
 * Try to print with thermal printer when available and connected (Web Bluetooth).
 * Returns true if printed via thermal printer, false otherwise (caller should use openPrintWindow).
 */
export async function tryPhomemoPrint(
  pdfBase64: string,
  options: {
    density?: number;
    barcodeSafeMode?: boolean;
    labelSpec?: import('../printers/phomemo/types').LabelSpec;
    onProgress?: (chunk: number, total: number) => void;
    signal?: AbortSignal;
  } = {}
): Promise<boolean> {
  try {
    const { isPhomemoAvailable, getConnectionState } = await import('../printers/phomemo/bleClient');
    const { printLabel } = await import('../printers/phomemo/phomemoPrint');
    if (!isPhomemoAvailable()) return false;
    const { state } = await getConnectionState();
    if (state !== 'connected') return false;
    await printLabel({
      pdfBase64,
      density: options.density,
      barcodeSafeMode: options.barcodeSafeMode,
      labelSpec: options.labelSpec,
      onProgress: options.onProgress,
      signal: options.signal,
    });
    return true;
  } catch {
    return false;
  }
}

/**
 * Print label: use thermal printer when available and connected, else open print window.
 * When usePhomemo and labelPdfData are provided, generates PDF with labelSpec and tries thermal printer first.
 */
export async function openPrintWindowOrPhomemo(
  html: string,
  options: {
    usePhomemo?: boolean;
    labelPdfData?: import('../printers/phomemo/labelPdf4x6').LabelPdf4x6Data;
    labelSpec?: import('../printers/phomemo/types').LabelSpec;
    phomemoDensity?: number;
    phomemoBarcodeSafeMode?: boolean;
    onProgress?: (chunk: number, total: number) => void;
    signal?: AbortSignal;
  } = {}
): Promise<void> {
  if (options.usePhomemo && options.labelPdfData) {
    const { generateLabelPdf } = await import('../printers/phomemo/labelPdf4x6');
    const { mergeLabelSpec } = await import('../printers/phomemo/types');
    const spec = mergeLabelSpec(options.labelSpec ?? null);
    const pdfBase64 = await generateLabelPdf(options.labelPdfData, spec);
    const printed = await tryPhomemoPrint(pdfBase64, {
      density: options.phomemoDensity,
      barcodeSafeMode: options.phomemoBarcodeSafeMode,
      labelSpec: spec,
      onProgress: options.onProgress,
      signal: options.signal,
    });
    if (printed) return;
  }
  openPrintWindow(html);
}
